import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
  ArrowLeft, Send, Upload, FileText, Download, MessageSquare, Users, Activity,
  UserPlus, Grid, List, RefreshCw, FileArchive, Search, Settings, Shield, Eye
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const DealRoomDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('documents');
  const [message, setMessage] = useState('');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentView, setDocumentView] = useState('grid');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [dealRoom, setDealRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageEncryption, setMessageEncryption] = useState('normal');

  useEffect(() => {
    fetchDealRoomData();
  }, [id]);

  const fetchDealRoomData = async () => {
    try {
      // Fetch deal room details
      const roomResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/deal-rooms/${id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const roomData = await roomResponse.json();
      if (roomData.success) {
        setDealRoom(roomData.deal_room);
      }

      // Fetch messages
      const messagesResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/deal-rooms/${id}/messages`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const messagesData = await messagesResponse.json();
      if (messagesData.success) {
        setMessages(messagesData.messages);
      }

      // Fetch files
      const filesResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/deal-rooms/${id}/files`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const filesData = await filesResponse.json();
      if (filesData.success) {
        setDocuments(filesData.files.map(f => ({
          id: f.id,
          name: f.file_name,
          category: f.category,
          size: f.file_size,
          status: f.status,
          version: f.version,
          uploadedBy: f.uploaded_by,
          date: new Date(f.uploaded_at).toLocaleDateString()
        })));
      }
    } catch (error) {
      console.error('Error fetching deal room data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Use mock data as fallback if backend fetch fails
  const dealRoomData = dealRoom || {
    id, name: 'Complex Implant Case - Dr. Johnson',
    description: 'Full arch implant planning and coordination', status: 'active',
    participants: [
      { id: '1', name: 'Dr. William Gray', role: 'Owner', email: 'wgray@stloralsurgery.com', permissions: 'Full access & management', active: true },
      { id: '2', name: 'Dr. Sarah Johnson', role: 'Collaborator', email: 'sjohnson@dental.com', permissions: 'Read, write, comment', active: true },
      { id: '3', name: 'Emma Williams', role: 'Patient', email: 'emma.w@email.com', permissions: 'Read only', active: false }
    ]
  };

  const isParticipant = dealRoomData.participants?.some(p => p.email === user?.email);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading deal room...</p>
        </div>
      </div>
    );
  }

  if (!isParticipant && user?.role !== 'superadmin' && user?.role !== 'admin') {
    return (
      <div className="max-w-4xl mx-auto">
        <Card className="border-2 border-red-500">
          <CardContent className="pt-6 text-center py-12">
            <div className="text-red-500 text-6xl mb-4">⛔</div>
            <h2 className="text-2xl font-bold text-[#0A1628] mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-6">You are not a participant in this deal room.</p>
            <Button onClick={() => navigate('/deal-rooms')}>
              <ArrowLeft className="h-4 w-4 mr-2" />Back to Deal Rooms
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const documentCategories = [
    { id: 'all', name: 'All Documents', count: 24 },
    { id: 'treatment', name: 'Treatment Plans', count: 8 },
    { id: 'imaging', name: 'Imaging & X-rays', count: 6 },
    { id: 'lab', name: 'Lab Results', count: 4 },
    { id: 'notes', name: 'Consultation Notes', count: 6 }
  ];

  const mockDocuments = [
    { id: 1, name: 'Treatment Plan - Phase 1.pdf', category: 'treatment', size: '2.4 MB', status: 'approved', version: 'v2.1', uploadedBy: 'Dr. William Gray', date: '2025-11-05' },
    { id: 2, name: 'Panoramic X-ray - November 2025.dcm', category: 'imaging', size: '15.2 MB', status: 'review', version: 'v1.0', uploadedBy: 'Dr. Sarah Johnson', date: '2025-11-06' },
    { id: 3, name: 'Blood Work Results - CBC Panel.pdf', category: 'lab', size: '890 KB', status: 'final', version: 'v1.0', uploadedBy: 'Dr. William Gray', date: '2025-11-07' },
    { id: 4, name: 'Initial Consultation Notes.pdf', category: 'notes', size: '1.1 MB', status: 'draft', version: 'v1.2', uploadedBy: 'Dr. Sarah Johnson', date: '2025-11-03' },
    { id: 5, name: '3D Scan - Upper Arch.stl', category: 'imaging', size: '8.7 MB', status: 'approved', version: 'v1.0', uploadedBy: 'Dr. William Gray', date: '2025-11-04' },
    { id: 6, name: 'Implant Positioning Guide.pdf', category: 'treatment', size: '3.2 MB', status: 'review', version: 'v1.0', uploadedBy: 'Dr. Sarah Johnson', date: '2025-11-05' }
  ];

  // Use fetched documents or fallback to mock
  const displayDocuments = documents.length > 0 ? documents : mockDocuments;

  const [auditLog] = useState([
    { id: 1, user: 'Dr. Sarah Johnson', action: 'uploaded', item: 'Treatment Plan - Phase 1.pdf', timestamp: 'Nov 9, 04:30 PM', ip: '192.168.1.100', device: 'Chrome 119.0 on Windows 11' },
    { id: 2, user: 'Dr. Michael Chen', action: 'viewed', item: 'Panoramic X-ray - November 2025.dcm', timestamp: 'Nov 9, 04:15 PM', ip: '192.168.1.105', device: 'Safari 17.0 on macOS Sonoma' },
    { id: 3, user: 'Jennifer Martinez', action: 'commented on', item: 'Blood Work Results - CBC Panel.pdf', timestamp: 'Nov 9, 03:45 PM', ip: '192.168.1.110', device: 'Chrome 119.0 on Android 14' },
    { id: 4, user: 'Dr. Sarah Johnson', action: 'invited', item: 'Dr. Robert Martinez (robert.martinez@orthodontics.com)', timestamp: 'Nov 9, 03:20 PM', ip: '192.168.1.100', device: 'Chrome 119.0 on Windows 11' }
  ]);

  const handleSendMessage = async () => {
    if (!message.trim()) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/deal-rooms/${id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          deal_room_id: id,
          message: message,
          is_encrypted: messageEncryption === 'encrypted'
        })
      });

      const data = await response.json();
      if (data.success) {
        const newMessage = {
          id: data.message.id,
          user: data.message.user_name,
          content: data.message.message,
          timestamp: new Date(data.message.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          isCurrentUser: true,
          isEncrypted: data.message.is_encrypted
        };
        setMessages([...messages, newMessage]);
        setMessage('');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Fallback to local state
      const newMessage = {
        id: messages.length + 1,
        user: user?.name || 'You',
        content: message,
        timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        isCurrentUser: true
      };
      setMessages([...messages, newMessage]);
      setMessage('');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/deal-rooms/${id}/files`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          deal_room_id: id,
          file_name: file.name,
          file_size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
          category: 'treatment',
          status: 'draft',
          version: 'v1.0'
        })
      });

      const data = await response.json();
      if (data.success) {
        const newDoc = {
          id: data.file.id,
          name: data.file.file_name,
          category: data.file.category,
          size: data.file.file_size,
          status: data.file.status,
          version: data.file.version,
          uploadedBy: data.file.uploaded_by,
          date: new Date(data.file.uploaded_at).toLocaleDateString()
        };
        setDocuments([...documents, newDoc]);
        alert('File uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      // Fallback to local state
      const newDoc = {
        id: documents.length + 1,
        name: file.name,
        category: 'treatment',
        size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
        status: 'draft',
        version: 'v1.0',
        uploadedBy: user?.name || 'You',
        date: new Date().toISOString().split('T')[0]
      };
      setDocuments([...documents, newDoc]);
    }
  };

  const handleInviteParticipant = () => {
    if (!inviteEmail) { alert('Please enter an email address'); return; }
    alert(`Invitation sent to ${inviteEmail} as ${inviteRole}`);
    setShowInviteModal(false);
    setInviteEmail('');
    setInviteRole('viewer');
  };

  const filteredDocuments = selectedCategory === 'all' ? displayDocuments : displayDocuments.filter(d => d.category === selectedCategory);
  const getStatusColor = (status) => ({ approved: 'bg-green-100 text-green-800', review: 'bg-yellow-100 text-yellow-800', final: 'bg-blue-100 text-blue-800', draft: 'bg-gray-100 text-gray-800' }[status] || 'bg-gray-100 text-gray-800');

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <Button variant="ghost" onClick={() => navigate('/deal-rooms')}>
          <ArrowLeft className="h-4 w-4 mr-2" />Back to Deal Rooms
        </Button>
        <Badge className="bg-green-100 text-green-800">{dealRoomData.status}</Badge>
      </div>

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[#0A1628] mb-2">{dealRoomData.name}</h1>
        <p className="text-gray-600">{dealRoomData.description}</p>
      </div>

      <div className="flex space-x-1 mb-6 border-b">
        {['documents', 'participants', 'messages', 'activity'].map(tab => (
          <Button key={tab} variant="ghost" className={`rounded-none pb-3 ${activeTab === tab ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-600'}`} onClick={() => setActiveTab(tab)}>
            {tab === 'documents' && <FileText className="h-4 w-4 mr-2" />}
            {tab === 'participants' && <Users className="h-4 w-4 mr-2" />}
            {tab === 'messages' && <MessageSquare className="h-4 w-4 mr-2" />}
            {tab === 'activity' && <Activity className="h-4 w-4 mr-2" />}
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </Button>
        ))}
      </div>

      {activeTab === 'documents' && (
        <div className="grid lg:grid-cols-4 gap-6">
          <Card className="lg:col-span-1">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Document Library</CardTitle>
                <div className="flex space-x-1">
                  <Button variant="ghost" size="sm" onClick={() => setDocumentView('grid')} className={documentView === 'grid' ? 'bg-blue-100' : ''}>
                    <Grid className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setDocumentView('list')} className={documentView === 'list' ? 'bg-blue-100' : ''}>
                    <List className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {documentCategories.map(cat => (
                <button key={cat.id} onClick={() => setSelectedCategory(cat.id)} className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition ${selectedCategory === cat.id ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'}`}>
                  <span className="text-sm font-medium">📁 {cat.name}</span>
                  <Badge variant="outline" className={selectedCategory === cat.id ? 'border-white text-white' : ''}>{cat.count}</Badge>
                </button>
              ))}
            </CardContent>
          </Card>

          <div className="lg:col-span-3">
            <div className="flex justify-end mb-4">
              <input type="file" id="docUpload" onChange={handleFileUpload} className="hidden" />
              <label htmlFor="docUpload">
                <Button as="span" className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B] cursor-pointer">
                  <Upload className="h-4 w-4 mr-2" />Upload Document
                </Button>
              </label>
            </div>

            {selectedDocument ? (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Document Viewer</CardTitle>
                    <Button variant="outline" size="sm" onClick={() => setSelectedDocument(null)}>Close</Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="bg-gray-100 rounded-lg p-12 text-center">
                    <FileText className="h-24 w-24 mx-auto mb-4 text-gray-400" />
                    <h3 className="text-xl font-bold mb-2">{selectedDocument.name}</h3>
                    <p className="text-gray-600 mb-4">{selectedDocument.size} • Version {selectedDocument.version} • {selectedDocument.status}</p>
                    <div className="flex justify-center space-x-2">
                      <Button className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]">
                        <Download className="h-4 w-4 mr-2" />Download
                      </Button>
                      <Button variant="outline">
                        <Eye className="h-4 w-4 mr-2" />Preview
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : filteredDocuments.length > 0 ? (
              documentView === 'grid' ? (
                <div className="grid md:grid-cols-3 gap-4">
                  {filteredDocuments.map(doc => (
                    <Card key={doc.id} className="cursor-pointer hover:shadow-lg transition" onClick={() => setSelectedDocument(doc)}>
                      <CardContent className="pt-6">
                        <div className="bg-gray-100 rounded-lg p-8 mb-3 flex items-center justify-center">
                          <FileText className="h-16 w-16 text-[#D4AF37]" />
                        </div>
                        <h4 className="font-semibold text-sm mb-2 truncate">{doc.name}</h4>
                        <div className="flex items-center justify-between text-xs mb-2">
                          <span className="text-gray-600">{doc.size}</span>
                          <Badge className={getStatusColor(doc.status)} style={{fontSize: '10px'}}>{doc.status}</Badge>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>📄 {doc.version}</span>
                          <span>{doc.date}</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredDocuments.map(doc => (
                    <Card key={doc.id} className="cursor-pointer hover:bg-gray-50" onClick={() => setSelectedDocument(doc)}>
                      <CardContent className="pt-4 pb-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <FileText className="h-10 w-10 text-[#D4AF37]" />
                            <div>
                              <h4 className="font-semibold text-sm">{doc.name}</h4>
                              <p className="text-xs text-gray-600">{doc.size} • {doc.uploadedBy} • {doc.date}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Badge className={getStatusColor(doc.status)}>{doc.status}</Badge>
                            <span className="text-xs text-gray-500">📄 {doc.version}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )
            ) : (
              <Card>
                <CardContent className="py-24 text-center">
                  <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">No Document Selected</h3>
                  <p className="text-gray-500">Select a document from the library to view it here</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {activeTab === 'participants' && (
        <div>
          <div className="flex justify-end mb-6">
            <Button className="bg-blue-500 text-white hover:bg-blue-600" onClick={() => setShowInviteModal(true)}>
              <UserPlus className="h-4 w-4 mr-2" />Invite
            </Button>
          </div>
          <Card className="mb-6">
            <CardHeader><CardTitle>Participants</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                {dealRoomData.participants.map(p => (
                  <div key={p.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-4">
                      <Avatar className="h-12 w-12">
                        <AvatarFallback className="bg-[#0A1628] text-[#D4AF37] font-bold">{p.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="flex items-center space-x-2">
                          <p className="font-semibold text-[#0A1628]">{p.name}</p>
                          <Badge variant="outline" className="text-xs">{p.role}</Badge>
                          {p.active && <div className="w-2 h-2 rounded-full bg-green-500"></div>}
                        </div>
                        <p className="text-sm text-gray-600">{p.email}</p>
                        <p className="text-xs text-gray-500 mt-1">{p.active ? 'Active now' : 'In 1h ago'}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm"><Settings className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Permission Levels</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  ['Owner:', 'Full access & management'],
                  ['Collaborator:', 'Read, write, comment'],
                  ['Specialist:', 'Read, write, comment'],
                  ['Patient:', 'Read only'],
                  ['Viewer:', 'Read only']
                ].map(([label, perm]) => (
                  <div key={label} className="flex justify-between items-center">
                    <span className="text-sm font-medium">{label}</span>
                    <span className="text-sm text-gray-600">{perm}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'messages' && (
        <div className="max-w-4xl mx-auto">
          <Card className="mb-6 bg-blue-50 border-blue-200">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start space-x-3">
                <Shield className="h-5 w-5 text-blue-600 mt-1" />
                <div>
                  <h3 className="font-semibold text-blue-900 mb-1">Security & Compliance</h3>
                  <p className="text-sm text-blue-800">This dealroom is protected with end-to-end encryption and complies with HIPAA regulations. All activities are logged for audit purposes.</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center">
                  <MessageSquare className="h-5 w-5 mr-2 text-[#D4AF37]" />Secure Messages
                  <span className="ml-2 text-sm text-gray-500">End-to-end encrypted • HIPAA compliant</span>
                </CardTitle>
                <div className="flex space-x-2">
                  <Button variant="outline" size="sm"><Search className="h-4 w-4 mr-2" />Search</Button>
                  <Button variant="outline" size="sm"><FileArchive className="h-4 w-4 mr-2" />Archive</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 mb-4 max-h-96 overflow-y-auto bg-gray-50 rounded-lg p-4">
                {messages.map(msg => (
                  <div key={msg.id} className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-start space-x-3">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="bg-[#0A1628] text-[#D4AF37]">{msg.user.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="font-semibold text-sm">{msg.user}</span>
                          {msg.id === 1 && <Badge className="bg-red-100 text-red-800 text-xs">high</Badge>}
                          <span className="text-xs text-gray-500">{msg.timestamp}</span>
                        </div>
                        <p className="text-sm text-gray-700">{msg.content}</p>
                        <Button variant="link" className="p-0 h-auto text-xs mt-2">Reply</Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <select 
                  className="px-3 py-2 border rounded-md text-sm w-32"
                  value={messageEncryption}
                  onChange={(e) => setMessageEncryption(e.target.value)}
                >
                  <option value="normal">Normal</option>
                  <option value="encrypted">Encrypted</option>
                </select>
                <div className="flex space-x-2">
                  <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Type your secure message..." rows={3} className="flex-1" />
                  <div className="flex flex-col space-y-2">
                    <Button onClick={handleSendMessage} className="bg-blue-500 text-white hover:bg-blue-600">
                      <Send className="h-4 w-4" />
                    </Button>
                    <Button variant="outline"><Upload className="h-4 w-4" /></Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'activity' && (
        <div className="max-w-6xl mx-auto">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Activity Audit Trail</CardTitle>
                <div className="flex items-center space-x-2">
                  <Button variant="outline" size="sm"><FileArchive className="h-4 w-4 mr-2" />Export</Button>
                  <Button variant="outline" size="sm"><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4 flex items-center space-x-4">
                <select className="px-3 py-2 border rounded-md text-sm">
                  <option>All Activities</option>
                  <option>Uploads</option>
                  <option>Views</option>
                </select>
                <select className="px-3 py-2 border rounded-md text-sm">
                  <option>Today</option>
                  <option>This Week</option>
                </select>
                <div className="flex items-center space-x-2">
                  <Shield className="h-4 w-4 text-blue-600" />
                  <span className="text-sm text-blue-600">HIPAA Compliant Logging</span>
                </div>
              </div>
              <div className="space-y-3">
                {auditLog.map(log => (
                  <div key={log.id} className="flex items-start space-x-4 p-4 border rounded-lg hover:bg-gray-50">
                    <div className="flex-shrink-0">
                      {log.action === 'uploaded' && <Upload className="h-5 w-5 text-green-600" />}
                      {log.action === 'viewed' && <Eye className="h-5 w-5 text-blue-600" />}
                      {log.action === 'commented on' && <MessageSquare className="h-5 w-5 text-purple-600" />}
                      {log.action === 'invited' && <UserPlus className="h-5 w-5 text-[#D4AF37]" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm"><span className="font-semibold">{log.user}</span> {log.action} <span className="font-medium">{log.item}</span></p>
                      <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                        <span>⏰ {log.timestamp}</span>
                        <span>🌐 {log.ip}</span>
                        <span>💻 {log.device}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 grid md:grid-cols-4 gap-4">
                {[['24', 'Total Activities'], ['5', 'Active Users'], ['8', 'Documents Accessed'], ['100%', 'Compliance Score']].map(([val, label]) => (
                  <Card key={label}>
                    <CardContent className="pt-6 text-center">
                      <p className={`text-3xl font-bold ${label === 'Compliance Score' ? 'text-green-600' : 'text-[#0A1628]'}`}>{val}</p>
                      <p className="text-sm text-gray-600">{label}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4">Invite Participant</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Email Address</label>
                <Input type="email" placeholder="doctor@example.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Role</label>
                <select className="w-full px-3 py-2 border rounded-md" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                  <option value="owner">Owner - Full access & management</option>
                  <option value="collaborator">Collaborator - Read, write, comment</option>
                  <option value="specialist">Specialist - Read, write, comment</option>
                  <option value="patient">Patient - Read only</option>
                  <option value="viewer">Viewer - Read only</option>
                </select>
              </div>
              <div className="flex space-x-2 pt-4">
                <Button className="flex-1 bg-blue-500 text-white hover:bg-blue-600" onClick={handleInviteParticipant}>Send Invite</Button>
                <Button variant="outline" className="flex-1" onClick={() => setShowInviteModal(false)}>Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealRoomDetail;
